import React, { useEffect, useState } from "react";

import { ConfigProvider, App as AntApp } from "antd";
import { BrowserRouter, Route, Routes, Navigate, useLocation, useNavigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useUserPreferences, PreferencesInitializer } from "./hooks/useUserPreferences";
import { NotificationProvider } from "./context/NotificationContext";
import { MessagingProvider } from "./context/MessagingContext";
import { XoeFocusProvider } from "./context/XoeFocusContext";
import "./App.css";
import "./styles/quill-theme.css";
import "./styles/global.css";

// UI gates from the session's permissions (contract §6.4)
import {
	canAccessResearch,
	canAccessXoe,
	canRaiseInvoices,
	canReviewHiAlerts,
	canUseGeneCodesWheel,
	canViewAppointments,
	canViewCommunications,
	canViewFormsInTray,
	canViewLabOrders,
	canViewLabProcessing,
	canViewLabTracking,
	canViewPatients,
	canViewPayments,
	canViewSubscriptions,
	canViewTestOrders,
	canViewTreatmentPlans,
	hasInventoryAccess,
	isPatientSession,
} from "./utils/userPrefs/validatePermissions";
import { resolvePatientFormRoute } from "./utils/patientFormRouteGuard";
import { resolveAuthenticatedHome } from "./utils/auth/authenticatedHomeRoute";
import { isCustomerDirectoryMember, isCustomerRoute } from "./utils/customer/customerDirectory";
import { isHandoverRoute } from "./utils/forms/handoverSession";
import { isTokenisedBookingRoute } from "./utils/bookingLinkRouteGuard";
import { hasActivePlans } from "./utils/subscriptions/subscriptionService";

//import pages
import LoginPage from "./pages/LoginPage";
import Logout from "./components/Logout";

import DashboardPage from "./pages/dashboard/DashboardPage";

//User Pages and settings
import ProfilePage from "./pages/profile/Profile";
import HelpPage from "./pages/help/HelpPage";
import GuideCategoryPage from "./pages/help/guides/GuideCategoryPage";
import GuideArticlePage from "./pages/help/guides/GuideArticlePage";

// Lab Pages
import LabOrdersPage from "./pages/lab/labOrders/LabOrdersPage";
import SampleProcessingPage from "./pages/lab/processing/SampleProcessingPage";
import SampleTrackingPage from "./pages/lab/tracking/SampleTrackingPage";

//Gene.Codes builder Page
import WheelPage from "./pages/gene.codes/GeneticWheel/WheelDashboard";
import { canAuthorWheels } from "./utils/geneCodes/wheelAuthoring";

//Research dashboard
import ResearchDashboardPage from "./pages/research/ResearchDashboardPage";

//Inventory/commerce system
import ProductsPage from "./pages/inventory/products/ProductsPage";
import OrdersPage from "./pages/inventory/orders/OrdersPage";
import CustomersPage from "./pages/inventory/customers/CustomersPage";

//Healthcare System
import PatientManagementPage from "./pages/healthcare/patient/PatientsPage";
import AppointmentManagementPage from "./pages/healthcare/appointments/AppointmentsPage";
import ResultsPage from "./pages/healthcare/results/ResultsPage";
import FormsInTrayPage from "./pages/healthcare/formsInTray/FormsInTrayPage";
import CommunicationsPage from "./pages/healthcare/communications/CommunicationsPage";
import InTrayPage from "./pages/healthcare/intray/InTrayPage";

// Treatment Plans
import TreatmentPlanDashboardPage from "./pages/healthcare/treatmentPlans/TreatmentPlanDashboardPage";
import PeptideDashboardPage from "./pages/healthcare/treatmentPlans/PeptideDashboardPage";

//Consult
import PatientConsultation from "./pages/patient/Consultation";
import MobileMic from "./components/mobile/MobileMic";

// Patient interface
import PatientDashboard from "./pages/patient/Dashboard";
import ProductCatalogPage from "./pages/customer/ProductCatalogPage";
import CustomerOrdersPage from "./pages/customer/CustomerOrdersPage";
import CustomerProfilePage from "./pages/customer/CustomerProfilePage";
import FormRenderer from "./pages/patient/Form";

import RegisterKit from "./pages/patient/RegisterKit";
import OrderAssignmentPage from "./pages/patient/OrderAssignmentPage";
import BookingForm from "./pages/patient/BookingForm";

//Admin pages
import HiAlertReviewPage from "./pages/admin/hi-alerts/HiAlertReviewPage";


// Finance Pages
import PaymentsPage from "./pages/finance/payments/PaymentsPage";
import InvoicesPage from "./pages/finance/invoices/InvoicesPage";
// The public, unauthenticated invoice pay page (I9) — mounted OUTSIDE every guard and <Layout>.
import PublicInvoicePage from "./pages/finance/public/PublicInvoicePage";
import SubscriptionPage from "./pages/finance/subscription/SubscriptionsPage";


// Xoe AI assistant widget (root-mounted — visible on all authenticated clinical pages)
import XoeWidget from "./components/xoe/XoeWidget";
// Clinic branding (S6) — public, pre-auth fetch; must wrap the login and booking-form routes
import { BrandingProvider, useBranding } from "./context/BrandingContext";
// Clinic branding, runtime application (S7) — the brand colour has to be re-asserted after
// every applyFullTheme, or the first accent change silently reverts it.
import { reapplyBrandColor } from "./utils/branding/applyBranding";
// Settings drawer (S1) — root-mounted, opened from the sidebar footer's profile dropdown
import { SettingsProvider } from "./components/settings/SettingsContext";
import SettingsModal from "./components/settings/SettingsModal";
// Xoe AI assistant, full-page tab (X9) — the widget's sibling entry point, INSIDE <Layout>
import XoeAssistantPage from "./pages/xoe/XoeAssistantPage";
// The route string the widget's suppression rule also reads — see the /xoe <Route> below
import { ErrorBoundary } from "./components/shared";
import { ENV } from "./config/runtimeEnv";
import { signOut } from "./utils/auth/sessionUtilities";
import { getSessionUser, useSessionUser } from "./utils/auth/sessionUser";
import SessionBootstrap from "./components/auth/SessionBootstrap";
import RouteGuard, { useSessionGate } from "./components/auth/RouteGuard";
import { isEnrolmentConfined, subscribeAuthSession, useAuthSession } from "./lib/http/session";
import { ENROLMENT_PATH } from "./lib/http/stepup";
import StepUpHost from "./components/auth/mfa/StepUpHost";
import IdleSignOutHost from "./components/auth/IdleSignOutHost";
import SecurityPage from "./pages/account/Security/SecurityPage";

const isCustomerUser = (): boolean => isCustomerDirectoryMember(getSessionUser());

// Role-based redirect component for root path
const RoleBasedRedirect = () => {
	const gate = useSessionGate();
	const sessionUser = useSessionUser();

	if (gate === "login") {
		return <Navigate to="/login" replace />;
	}
	if (gate === "enrol") return <Navigate to={ENROLMENT_PATH} replace />;
	if (gate === "wait" || !sessionUser) return <SessionBootstrap />;

	try {
		const user = sessionUser;

		const home = resolveAuthenticatedHome(user);
		if (home !== "/") {
			return <Navigate to={home} replace />;
		}

		return <DashboardPage />;
	} catch (error) {
		console.error("Error parsing user data:", error);
		return <Navigate to="/login" replace />;
	}
};

const PatientFormRoute = ({ children }: { children: JSX.Element }) => {
	const token = new URLSearchParams(window.location.search).get("t");
	const signedIn = useAuthSession().status === "authenticated";

	return resolvePatientFormRoute({ token, signedIn, patientSession: isPatientSession() }) === "redirect-patient-dashboard"
		? <Navigate to="/patient" replace />
		: children;
};

const useSubscriptionGuard = () => {
	const navigate = useNavigate();
	const profile = useSessionUser();
	const signedIn = useAuthSession().status === "authenticated";

	useEffect(() => {
		if (!signedIn || !profile) return;

		if (isPatientSession()) return;

		if (!hasActivePlans()) {
			void signOut().finally(() => navigate("/login", { replace: true }));
		}
	}, [signedIn, profile?.uuid]);
};

// Main App component with updated lab workflow
const AppContent: React.FC = () => {
	useSubscriptionGuard();

	const location = useLocation();
	const inHandover = isHandoverRoute(location.pathname, location.search);
	const onBookingLink = isTokenisedBookingRoute(location.pathname, location.search);
	const onPublicInvoice = location.pathname === "/invoice";
	// the wheel builder hosts its own inline Xoe pane, so the floating widget stands downthere 
	const onWheelBuilder = location.pathname === "/gene.codes/wheel/build";
	// the customer surface never carries Xoe, whoever is signed in.
	const onCustomerSurface = isCustomerRoute(location.pathname);
	// A session confined to MFA enrolment has only the security page; the widget and drawer would call refused routes.
	const confined = isEnrolmentConfined(useAuthSession().info);

	return (
		<>
			<ErrorBoundary>
				<Routes>

					{/* Public Route */}
					<Route path="/login" element={<LoginPage />} />
					<Route path="/mobile-mic" element={<MobileMic />} />
					<Route path="/logout" element={<Logout />} />
					<Route path={ENROLMENT_PATH} element={<SecurityPage />} />


					{/* Protected Routes */}
					<Route path="/" element={<RoleBasedRedirect />} />


					{/********************************************* Clinical routes */}
					<Route
						path={"/xoe"}
						element={
							<RouteGuard allow={canAccessXoe}>
								<XoeAssistantPage />
							</RouteGuard>
						}
					/>

					<Route
						path="/profile"
						element={
							<RouteGuard prefetchBlood>
								<ProfilePage />
							</RouteGuard>
						}
					/>
					<Route
						path="/help"
						element={
							<RouteGuard prefetchBlood>
								<HelpPage />
							</RouteGuard>
						}
					/>
					<Route
						path="/help/guides"
						element={
							<RouteGuard prefetchBlood>
								<HelpPage />
							</RouteGuard>
						}
					/>
					<Route
						path="/help/guides/:category"
						element={
							<RouteGuard prefetchBlood>
								<GuideCategoryPage />
							</RouteGuard>
						}
					/>
					<Route
						path="/help/guides/:category/:slug"
						element={
							<RouteGuard prefetchBlood>
								<GuideArticlePage />
							</RouteGuard>
						}
					/>


					{/****************************************************** Admin for the system */}
					<Route
						path="/admin/hi-alerts"
						element={
							<RouteGuard allow={canReviewHiAlerts}>
								<HiAlertReviewPage />
							</RouteGuard>
						}
					/>

					{/******************************************************* Healthcare */}
					<Route
						path="/healthcare/patients"
						element={
							<RouteGuard allow={canViewPatients} feature={["healthcare", "healthcare.peptide"]}>
								<PatientManagementPage />
							</RouteGuard>
						}
					/>
					<Route
						path="/healthcare/appointments"
						element={
							<RouteGuard allow={canViewAppointments} feature="healthcare">
								<AppointmentManagementPage />
							</RouteGuard>
						}
					/>
					<Route
						path="/healthcare/communications"
						element={
							<RouteGuard allow={canViewCommunications} feature="healthcare">
								<CommunicationsPage />
							</RouteGuard>
						}
					/>
					<Route
						path="/healthcare/results"
						element={
							<RouteGuard allow={canViewTestOrders} feature="healthcare">
								<ResultsPage />
							</RouteGuard>
						}
					/>
					<Route
						path="/healthcare/intray"
						element={
							<RouteGuard allow={canViewTestOrders} feature="healthcare">
								<InTrayPage />
							</RouteGuard>
						}
					/>
					<Route
						path="/healthcare/forms-intray"
						element={
							<RouteGuard allow={canViewFormsInTray} feature="healthcare">
								<FormsInTrayPage />
							</RouteGuard>
						}
					/>
					<Route
						path="/healthcare/treatment-plans"
						element={
							<RouteGuard allow={canViewTreatmentPlans} feature="healthcare">
								<TreatmentPlanDashboardPage />
							</RouteGuard>
						}
					/>
					<Route
						path="/healthcare/peptide-dashboard"
						element={
							<RouteGuard allow={canViewTreatmentPlans} feature="healthcare.peptide">
								<PeptideDashboardPage />
							</RouteGuard>
						}
					/>

					{/******************************************************** Lab Management Routes */}
					<Route
						path="/lab/orders"
						element={
							<RouteGuard allow={canViewLabOrders} feature="laboratory">
								<LabOrdersPage />
							</RouteGuard>
						}
					/>
					<Route
						path="/lab/processing"
						element={
							<RouteGuard allow={canViewLabProcessing} feature="laboratory">
								<SampleProcessingPage />
							</RouteGuard>
						}
					/>
					<Route
						path="/lab/tracking/:id?"
						element={
							<RouteGuard allow={canViewLabTracking} feature="laboratory">
								<SampleTrackingPage />
							</RouteGuard>
						}
					/>
					{/* Gene.Codes Routes */}
					<Route
						path="/gene.codes/wheel"
						element={
							<RouteGuard allow={canUseGeneCodesWheel}>
								<WheelPage />
							</RouteGuard>
						}
					/>
					<Route
						path={"/gene.codes/wheel/build"}
						element={
							<RouteGuard allow={canUseGeneCodesWheel}>
								<RouteGuard allow={canAuthorWheels} redirectTo="/gene.codes/wheel">
									<WheelPage mode="build" />
								</RouteGuard>
							</RouteGuard>
						}
					/>
					{/******************************************************* Research dashboard */}
					<Route
						path="/research"
						element={
							<RouteGuard allow={canAccessResearch}>
								<ResearchDashboardPage />
							</RouteGuard>
						}
					/>
					{/******************************************************* Inventory & Commerce Routes */}
					<Route
						path="/inventory/products"
						element={
							<RouteGuard allow={hasInventoryAccess}>
								<ProductsPage />
							</RouteGuard>
						}
					/>
					<Route
						path="/inventory/orders"
						element={
							<RouteGuard allow={hasInventoryAccess}>
								<OrdersPage />
							</RouteGuard>
						}
					/>
					<Route
						path="/inventory/customers"
						element={
							<RouteGuard allow={hasInventoryAccess}>
								<CustomersPage />
							</RouteGuard>
						}
					/>
					{/******************************************************* Finance routes */}
					<Route
						path="/finance/payments"
						element={
							<RouteGuard allow={canViewPayments}>
								<PaymentsPage />
							</RouteGuard>
						}
					/>
					<Route
						path="/finance/invoices"
						element={
							<RouteGuard allow={canRaiseInvoices}>
								<InvoicesPage />
							</RouteGuard>
						}
					/>
					<Route
						path="/finance/subscriptions"
						element={
							<RouteGuard allow={canViewSubscriptions}>
								<SubscriptionPage />
							</RouteGuard>
						}
					/>

					{/************************************************ Customer Routes  */}
					<Route
						path="/customer"
						element={
							<RouteGuard allow={isCustomerUser}>
								<Navigate to="/customer/products" replace />
							</RouteGuard>
						}
					/>
					<Route
						path="/customer/products"
						element={
							<RouteGuard allow={isCustomerUser}>
								<ProductCatalogPage />
							</RouteGuard>
						}
					/>
					<Route
						path="/customer/orders"
						element={
							<RouteGuard allow={isCustomerUser}>
								<CustomerOrdersPage />
							</RouteGuard>
						}
					/>
					<Route
						path="/customer/profile"
						element={
							<RouteGuard allow={isCustomerUser}>
								<CustomerProfilePage />
							</RouteGuard>
						}
					/>
					{/******************************************************* Patient routes */}
					<Route
						path="/patient"
						element={
							<RouteGuard allow={isPatientSession} redirectTo="/login">
								<PatientDashboard />
							</RouteGuard>
						}
					/>
					<Route
						path="/patient/form"
						element={
							<PatientFormRoute>
								<FormRenderer />
							</PatientFormRoute>
						}
					/>
					<Route
						path="/patient/booking-form"
						element={
							<PatientFormRoute>
								<BookingForm />
							</PatientFormRoute>
						}
					/>
					<Route path="/r" element={<RegisterKit />} />
					<Route path={"/invoice"} element={<PublicInvoicePage />} />
					<Route
						path="/patient/test-order-assignments/:id"
						element={
							<PatientFormRoute>
								<OrderAssignmentPage />
							</PatientFormRoute>
						}
					/>
					{/*  Consultation routes */}
					<Route path="/patient/consultation/:roomId" element={<PatientConsultation />} />
					{/* Fallback Route */}
					<Route path="*" element={<Navigate to="/" replace />} />
				</Routes>
			</ErrorBoundary>

			{ENV.VITE_XOE_ENABLED === "true" && !confined && !inHandover && !onBookingLink && !onPublicInvoice && !onWheelBuilder && !onCustomerSurface && <XoeWidget />}

			{!confined && !inHandover && !onBookingLink && !onPublicInvoice && <SettingsModal />}

			<StepUpHost />
			<IdleSignOutHost />
		</>
	);
};

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 5 * 60 * 1000,
			gcTime: 10 * 60 * 1000,
			retry: 1,
			refetchOnWindowFocus: false,
		},
	},
});

/**
 * Ant Design's ConfigProvider, with the clinic's primary colour merged over the user's accent
 */
const BrandedConfigProvider: React.FC<{
	theme: React.ComponentProps<typeof ConfigProvider>["theme"];
	children: React.ReactNode;
}> = ({ theme, children }) => {
	const { branding } = useBranding();

	const themed = React.useMemo(
		() =>
			theme && branding.whitelabel && branding.primary_color
				? { ...theme, token: { ...theme.token, colorPrimary: branding.primary_color } }
				: theme,
		[theme, branding.whitelabel, branding.primary_color],
	);

	return <ConfigProvider theme={themed}>{children}</ConfigProvider>;
};

/**
 * Themed App Wrapper
 * Handles dynamic theme updates and Ant Design configuration
 */
const ThemedApp: React.FC = () => {
	const { themeConfig, isLoaded } = useUserPreferences();
	const [currentTheme, setCurrentTheme] = useState(themeConfig);

	// Cached server data belongs to the session that fetched it; the next user in this tab must not see it.
	useEffect(
		() =>
			subscribeAuthSession((next, previous) => {
				if (previous.status !== "authenticated") return;
				// A sign-in in another tab can swap the shared cookies for another account without this tab ever leaving "authenticated".
				if (next.status !== "authenticated" || next.info?.user.uuid !== previous.info?.user.uuid) queryClient.clear();
			}),
		[]
	);

	// Update theme when preferences change
	useEffect(() => {
		if (themeConfig) {
			setCurrentTheme(themeConfig);
		}
	}, [themeConfig]);

	// Listen for theme change events
	useEffect(() => {
		const handleThemeChange = (event: Event) => {
			const customEvent = event as CustomEvent;
			// Update theme dynamically without full page reload
			const newPrefs = customEvent.detail;
			if (newPrefs) {
				// Import theme utilities and apply changes
				import("./utils/userPrefs/themeUtils").then(({ applyFullTheme }) => {
					applyFullTheme(newPrefs.theme as "light" | "dark", newPrefs.accentColor as "blue" | "purple" | "green" | "teal" | "amber" | "pink")
						.then((config) => {
							reapplyBrandColor();
							setCurrentTheme(config);
						})
						.catch((error) => {
							console.error("Failed to apply theme change:", error);
						});
				});
			}
		};

		window.addEventListener("themeChange", handleThemeChange);
		return () => window.removeEventListener("themeChange", handleThemeChange);
	}, []);

	// Show loading state while theme initializes
	if (!isLoaded || !currentTheme) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
					background: "#f0f2f5",
				}}
			>
				<div>Loading...</div>
			</div>
		);
	}

	return (
		<QueryClientProvider client={queryClient}>
			<BrandingProvider>
				<BrandedConfigProvider theme={currentTheme}>
					<AntApp>
						<SessionBootstrap>
							<MessagingProvider>
								<NotificationProvider>
									<BrowserRouter>
										<XoeFocusProvider>
											<SettingsProvider>
												{/* will move layout here */}
												<AppContent />
											</SettingsProvider>
										</XoeFocusProvider>
									</BrowserRouter>
								</NotificationProvider>
							</MessagingProvider>
						</SessionBootstrap>
					</AntApp>
				</BrandedConfigProvider>
			</BrandingProvider>
		</QueryClientProvider>
	);
};

/**
 * Main App Component
 */
function App() {
	return (
		<>
			<PreferencesInitializer />
			<ThemedApp />
		</>
	);
}

export default App;
